import { apiService, ApiResponse } from './api';

export interface PaymentRequest {
  memberId: number;
  amount: number;
  paymentMethod: 'CASH' | 'CARD' | 'FUND_TRANSFER';
  paymentDate: string;
  notes?: string;
}

export interface PaymentResponse {
  id: number;
  member: {
    id: number;
    memberNumber: string;
    fullName: string;
    phone: string;
    membershipType: string;
    peakHours: string;
    nicPassport: string;
    startDate: string;
    status: 'ACTIVE' | 'SUSPENDED' | 'EXPIRED';
  };
  amount: number;
  paymentMethod: 'CASH' | 'CARD' | 'FUND_TRANSFER';
  paymentDate: string;
  transactionId: string;
  receiptToken: string;
  notes?: string;
  status: 'COMPLETED' | 'PENDING' | 'FAILED';
  createdAt: string;
  updatedAt: string;
}

export interface PaymentSummary {
  date: string;
  totalAmount: number;
  totalCount: number;
  cashAmount: number;
  cashCount: number;
  cardAmount: number;
  cardCount: number;
  fundTransferAmount: number;
  fundTransferCount: number;
}

export interface PaginatedPaymentResponse {
  content: PaymentResponse[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}

class PaymentService {

  async recordPayment(paymentData: PaymentRequest): Promise<PaymentResponse> {
    const response = await apiService.post<PaymentResponse>('/api/payments', paymentData);
    
    if (!response.success || !response.data) {
      throw new Error(response.message || 'Failed to record payment');
    }

    return response.data;
  }

  async updatePayment(paymentId: number, paymentData: PaymentRequest): Promise<PaymentResponse> {
    const response = await apiService.put<PaymentResponse>(`/api/payments/${paymentId}`, paymentData);

    if (!response.success || !response.data) {
      throw new Error(response.message || 'Failed to update payment');
    }

    return response.data;
  }

  async getPaymentById(id: number): Promise<PaymentResponse> {
    const response = await apiService.get<PaymentResponse>(`/api/payments/${id}`);
    
    if (!response.success || !response.data) {
      throw new Error(response.message || 'Payment not found');
    }

    return response.data;
  }

  async getPaymentByTransactionId(transactionId: string): Promise<PaymentResponse> {
    const response = await apiService.get<PaymentResponse>(`/api/payments/transaction/${transactionId}`);
    
    if (!response.success || !response.data) {
      throw new Error(response.message || 'Payment not found');
    }

    return response.data;
  }

  async getPaymentByReceiptToken(token: string): Promise<PaymentResponse> {
    const response = await apiService.get<PaymentResponse>(`/api/payments/receipt/${token}`);
    if (!response.success || !response.data) {
      throw new Error(response.message || 'Receipt not found');
    }
    return response.data;
  }

  async getAllPayments(page: number = 0, size: number = 10): Promise<PaginatedPaymentResponse> {
    const response = await apiService.get<PaginatedPaymentResponse>(`/api/payments?page=${page}&size=${size}`);
    
    if (!response.success || !response.data) {
      throw new Error(response.message || 'Failed to fetch payments');
    }

    return response.data;
  }

  async getPaymentsByMemberId(memberId: number, page: number = 0, size: number = 10): Promise<PaginatedPaymentResponse> {
    const response = await apiService.get<PaginatedPaymentResponse>(`/api/payments/member/${memberId}?page=${page}&size=${size}`);
    
    if (!response.success || !response.data) {
      throw new Error(response.message || 'Failed to fetch member payments');
    }

    return response.data;
  }

  async getPaymentsByDateRange(
    startDate: string, 
    endDate: string, 
    page: number = 0, 
    size: number = 10
  ): Promise<PaginatedPaymentResponse> {
    const response = await apiService.get<PaginatedPaymentResponse>(
      `/api/payments/date-range?startDate=${startDate}&endDate=${endDate}&page=${page}&size=${size}`
    );
    
    if (!response.success || !response.data) {
      throw new Error(response.message || 'Failed to fetch payments by date range');
    }

    return response.data;
  }

  async getPaymentSummaries(startDate: string, endDate: string): Promise<PaymentSummary[]> {
    const response = await apiService.get<PaymentSummary[]>(
      `/api/payments/summaries?startDate=${startDate}&endDate=${endDate}`
    );
    
    if (!response.success || !response.data) {
      throw new Error(response.message || 'Failed to fetch payment summaries');
    }

    return response.data;
  }

  async getTotalAmountByDateRange(startDate: string, endDate: string): Promise<{
    totalAmount: number;
    totalCount: number;
    startDate: string;
    endDate: string;
  }> {
    const response = await apiService.get<{
      totalAmount: number;
      totalCount: number;
      startDate: string;
      endDate: string;
    }>(`/api/payments/total-amount?startDate=${startDate}&endDate=${endDate}`);
    
    if (!response.success || !response.data) {
      throw new Error(response.message || 'Failed to fetch total amount');
    }

    return response.data;
  }

  async getRecentPayments(page: number = 0, size: number = 5): Promise<PaginatedPaymentResponse> {
    const response = await apiService.get<PaginatedPaymentResponse>(`/api/payments/recent?page=${page}&size=${size}`);
    
    if (!response.success || !response.data) {
      throw new Error(response.message || 'Failed to fetch recent payments');
    }

    return response.data;
  }

  async getLastPaymentByMember(memberId: number): Promise<PaymentResponse | null> {
    const response = await apiService.get<PaymentResponse>(`/api/payments/member/${memberId}/last`);
    
    // Return null if not found (404), throw error for other failures
    if (!response.success) {
      if (response.message?.includes('not found') || response.message?.includes('404')) {
        return null;
      }
      throw new Error(response.message || 'Failed to fetch last payment');
    }

    return response.data || null;
  }

  async getTodayPaymentSummary(): Promise<{
    date: string;
    totalAmount: number;
    totalCount: number;
  }> {
    const response = await apiService.get<{
      date: string;
      totalAmount: number;
      totalCount: number;
    }>('/api/payments/today');
    
    if (!response.success || !response.data) {
      throw new Error(response.message || 'Failed to fetch today\'s payment summary');
    }

    return response.data;
  }

  async getThisMonthPaymentSummary(): Promise<{
    startDate: string;
    endDate: string;
    totalAmount: number;
    totalCount: number;
  }> {
    const response = await apiService.get<{
      startDate: string;
      endDate: string;
      totalAmount: number;
      totalCount: number;
    }>('/api/payments/this-month');
    
    if (!response.success || !response.data) {
      throw new Error(response.message || 'Failed to fetch this month\'s payment summary');
    }

    return response.data;
  }

  async updatePaymentStatus(paymentId: number, status: 'COMPLETED' | 'PENDING' | 'FAILED'): Promise<PaymentResponse> {
    const response = await apiService.put<PaymentResponse>(`/api/payments/${paymentId}/status?status=${status}`, {});

    if (!response.success || !response.data) {
      throw new Error(response.message || 'Failed to update payment status');
    }

    return response.data;
  }
}

export default new PaymentService();
